import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, Grid,
  Select, MenuItem, FormControl, InputLabel, CircularProgress,
  Snackbar, Alert, Chip, Divider, Autocomplete, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Avatar, IconButton, Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Send, NotificationsActive, Person, Group, History,
  Refresh, Search,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { formatDateTime } from '../utils/dateUtils';

const EMPTY_BROADCAST = { title: '', message: '', type: 'general', target: 'all' };
const EMPTY_USER      = { title: '', message: '', type: 'general', userId: null };

const TYPE_COLOR = {
  general: 'default', announcement: 'primary', kyc: 'warning',
  vehicle: 'info', alert: 'error',
};
const TYPE_OPTS = ['general', 'announcement', 'kyc', 'vehicle', 'alert'];

// ── Small "compose" section label ─────────────────────────────────────────────
const SectionTitle = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
    <Box sx={{ bgcolor: '#1a237e18', borderRadius: 1.5, p: 1, display: 'flex' }}>
      {React.cloneElement(icon, { sx: { color: '#1a237e', fontSize: 22 } })}
    </Box>
    <Typography variant="subtitle1" fontWeight={700} color="#1a237e">{title}</Typography>
  </Box>
);

export default function Notifications() {
  const [tab, setTab]               = useState(0); // 0=Broadcast 1=User 2=History
  const [bForm, setBForm]           = useState(EMPTY_BROADCAST);
  const [uForm, setUForm]           = useState(EMPTY_USER);
  const [sending, setSending]       = useState(false);
  const [uSending, setUSending]     = useState(false);

  // history
  const [history, setHistory]       = useState([]);
  const [histFiltered, setHistFiltered] = useState([]);
  const [histTotal, setHistTotal]   = useState(0);
  const [page, setPage]             = useState(0);
  const [pageSize, setPageSize]     = useState(10);
  const [histLoading, setHistLoading] = useState(false);
  const [histSearch, setHistSearch] = useState('');

  // users for autocomplete
  const [users, setUsers]           = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [snack, setSnack]           = useState({ open: false, msg: '', sev: 'success' });

  // ── Fetch history ───────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    const res = await InvokeApi(Config.base_url + '/admin/notifications', {
      limit: 500, offset: 0,
    });
    if (res?.code === '200') {
      const list = (res.notifications ?? []).map((r, i) => ({
        ...r,
        id:           r.id ?? i + 1,
        sent_count:   r.sent_count   || 0,
        target_group: r.target_group || 'all',
      }));
      setHistory(list);
      setHistTotal(res.total ?? list.length);
    }
    setHistLoading(false);
  }, []);

  // ── Fetch users for autocomplete ────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.getUsers, { page: 1, per_page: 500 });
    if (res?.code === '200') {
      setUsers((res.users ?? []).map(u => ({
        id: u.id, name: u.name, phone: u.phone,
        label: `${u.name} (${u.phone})`,
      })));
    }
    setUsersLoading(false);
  }, []);

  useEffect(() => { fetchHistory(); fetchUsers(); }, [fetchHistory, fetchUsers]);

  // ── Local filter for history ────────────────────────────────────────────────
  useEffect(() => {
    const q = histSearch.toLowerCase();
    setHistFiltered(
      history.filter(r =>
        !q ||
        r.title?.toLowerCase().includes(q) ||
        r.message?.toLowerCase().includes(q) ||
        r.sent_by?.toLowerCase().includes(q)
      )
    );
    setPage(0);
  }, [histSearch, history]);

  const histPaginated = histFiltered.slice(page * pageSize, page * pageSize + pageSize);

  // ── Send broadcast ──────────────────────────────────────────────────────────
  const handleBroadcast = async () => {
    if (!bForm.title.trim() || !bForm.message.trim()) {
      setSnack({ open: true, msg: 'Title and message are required.', sev: 'warning' }); return;
    }
    setSending(true);
    const res = await InvokeApi(Config.base_url + apiList.sendNotification, {
      title: bForm.title, message: bForm.message,
      type: bForm.type,   target: bForm.target,
    });
    setSending(false);
    if (res?.code === '200' || res?.status === 'success') {
      const sent    = res.success_count ?? 0;
      const devices = res.total_devices ?? 0;
      setSnack({ open: true, sev: 'success', msg: devices > 0 ? `Sent to ${sent}/${devices} devices.` : (res.message || 'Sent successfully.') });
      setBForm(EMPTY_BROADCAST);
      fetchHistory();
    } else {
      setSnack({ open: true, msg: res?.message || 'Send failed.', sev: 'error' });
    }
  };

  // ── Send to user ────────────────────────────────────────────────────────────
  const handleUserSend = async () => {
    if (!uForm.title.trim() || !uForm.message.trim() || !uForm.userId) {
      setSnack({ open: true, msg: 'Please fill all fields and select a user.', sev: 'warning' }); return;
    }
    setUSending(true);
    const res = await InvokeApi(Config.base_url + '/admin/notifications/send-to-user', {
      user_id: uForm.userId,
      title:   uForm.title,
      message: uForm.message,
      type:    uForm.type,
    });
    setUSending(false);
    if (res?.code === '200') {
      const sent = res.fcm_sent ?? 0;
      const tot  = res.fcm_total ?? 0;
      setSnack({ open: true, sev: 'success', msg: tot > 0 ? `Sent to ${sent}/${tot} devices.` : 'Notification sent.' });
      setUForm(EMPTY_USER);
      fetchHistory();
    } else {
      setSnack({ open: true, msg: res?.message || 'Send failed.', sev: 'error' });
    }
  };

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Notification Center</Typography>
          <Typography variant="body2" color="text.secondary">
            {histTotal} notification{histTotal !== 1 ? 's' : ''} sent
          </Typography>
        </Box>
        <Tooltip title="Refresh History">
          <IconButton onClick={fetchHistory} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: 2, '& .MuiTab-root': { fontWeight: 700, fontSize: 13 }, borderBottom: '1px solid #f0f0f0' }}
        >
          <Tab icon={<Group fontSize="small" />}              iconPosition="start" label="Broadcast" />
          <Tab icon={<Person fontSize="small" />}             iconPosition="start" label="Send to User" />
          <Tab icon={<History fontSize="small" />}            iconPosition="start" label="History" />
        </Tabs>

        {/* ── Tab 0: Broadcast ──────────────────────────────────────────────── */}
        {tab === 0 && (
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SectionTitle icon={<NotificationsActive />} title="Send Broadcast Notification" />
                <TextField
                  fullWidth size="small" label="Title *"
                  value={bForm.title}
                  onChange={e => setBForm({ ...bForm, title: e.target.value })}
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 100 }}
                  helperText={`${bForm.title.length}/100`}
                />
                <TextField
                  fullWidth multiline rows={4} size="small" label="Message *"
                  value={bForm.message}
                  onChange={e => setBForm({ ...bForm, message: e.target.value })}
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 500 }}
                  helperText={`${bForm.message.length}/500`}
                />
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Notification Type</InputLabel>
                  <Select value={bForm.type} label="Notification Type"
                    onChange={e => setBForm({ ...bForm, type: e.target.value })}>
                    {TYPE_OPTS.map(t => (
                      <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                  <InputLabel>Target Audience</InputLabel>
                  <Select value={bForm.target} label="Target Audience"
                    onChange={e => setBForm({ ...bForm, target: e.target.value })}>
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="kyc_pending">Pending KYC Users</MenuItem>
                    <MenuItem value="active">Active Users Only</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained" fullWidth
                  startIcon={sending ? null : <Send />}
                  onClick={handleBroadcast}
                  disabled={sending}
                  sx={{ py: 1.3, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700 }}
                >
                  {sending ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send Broadcast'}
                </Button>
              </Grid>

              {/* Tip panel */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, p: 2.5, bgcolor: '#f8f9ff', height: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="#1a237e" sx={{ mb: 1.5 }}>
                    Broadcast Tips
                  </Typography>
                  {[
                    ['All Users',      'Sends to every registered user with a device token.'],
                    ['Pending KYC',    'Targets users who haven\'t completed KYC yet.'],
                    ['Active Only',    'Sends only to users with an active account status.'],
                    ['FCM Delivery',   'Push is delivered via Firebase Cloud Messaging. Delivery depends on device token availability.'],
                  ].map(([label, desc]) => (
                    <Box key={label} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1a237e', mt: 0.8, flexShrink: 0 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{label}</Typography>
                        <Typography variant="caption" color="text.secondary">{desc}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        )}

        {/* ── Tab 1: Send to User ───────────────────────────────────────────── */}
        {tab === 1 && (
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SectionTitle icon={<Person />} title="Send to Specific User" />
                <Autocomplete
                  options={users}
                  getOptionLabel={o => o.label}
                  value={users.find(u => u.id === uForm.userId) || null}
                  onChange={(_, v) => setUForm({ ...uForm, userId: v?.id || null })}
                  loading={usersLoading}
                  renderOption={(props, o) => (
                    <Box component="li" {...props} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: '#e8eaf6', color: '#1a237e', fontWeight: 700 }}>
                        {o.name[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography fontSize={13} fontWeight={600}>{o.name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{o.phone}</Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={params => (
                    <TextField {...params} label="Select User *" size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {usersLoading && <CircularProgress size={18} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth size="small" label="Title *"
                  value={uForm.title}
                  onChange={e => setUForm({ ...uForm, title: e.target.value })}
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 100 }}
                  helperText={`${uForm.title.length}/100`}
                />
                <TextField
                  fullWidth multiline rows={4} size="small" label="Message *"
                  value={uForm.message}
                  onChange={e => setUForm({ ...uForm, message: e.target.value })}
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 500 }}
                  helperText={`${uForm.message.length}/500`}
                />
                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                  <InputLabel>Notification Type</InputLabel>
                  <Select value={uForm.type} label="Notification Type"
                    onChange={e => setUForm({ ...uForm, type: e.target.value })}>
                    {TYPE_OPTS.map(t => (
                      <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained" fullWidth
                  startIcon={uSending ? null : <Send />}
                  onClick={handleUserSend}
                  disabled={uSending || !uForm.userId}
                  sx={{ py: 1.3, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700 }}
                >
                  {uSending ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send to User'}
                </Button>
              </Grid>

              {/* Selected user preview */}
              <Grid item xs={12} md={6}>
                {uForm.userId ? (
                  (() => {
                    const u = users.find(x => x.id === uForm.userId);
                    return u ? (
                      <Card elevation={0} sx={{ border: '1px solid #bfdbfe', borderRadius: 2, p: 2.5, bgcolor: '#eff6ff' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="#1e40af" sx={{ mb: 1.5 }}>
                          Sending to:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 48, height: 48, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 20, fontWeight: 700 }}>
                            {u.name[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={700}>{u.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{u.phone}</Typography>
                            <Typography variant="caption" color="#1a237e">ID: {u.id}</Typography>
                          </Box>
                        </Box>
                      </Card>
                    ) : null;
                  })()
                ) : (
                  <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, p: 2.5, bgcolor: '#f8f9ff', textAlign: 'center' }}>
                    <Person sx={{ fontSize: 40, color: '#c7d2fe', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Select a user from the dropdown to preview and send them a notification.
                    </Typography>
                  </Card>
                )}
              </Grid>
            </Grid>
          </CardContent>
        )}
      </Card>

      {/* ── Tab 2: History (always rendered below or as main tab) ─────────── */}
      {tab === 2 && (
        <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          {/* History filter bar */}
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid #f0f0f0' }}>
            <TextField
              size="small"
              placeholder="Search title, message or sender..."
              value={histSearch}
              onChange={e => setHistSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchHistory} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          {histLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress sx={{ color: '#1a237e' }} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f5f6fa' }}>
                      {['#', 'Title', 'Message', 'Type', 'Target', 'Devices', 'Sent By', 'Sent At'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {histPaginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                          No notifications sent yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      histPaginated.map((n) => (
                        <TableRow key={n.id} hover>
                          <TableCell>
                            <Chip label={`#${n.id}`} size="small"
                              sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 12 }} />
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={13} fontWeight={600}>{n.title || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={12} color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                              {n.message || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={n.type || 'general'}
                              color={TYPE_COLOR[n.type] || 'default'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={n.target_group === 'single_user' ? 'Single User' : (n.target_group || 'all')}
                              variant="outlined"
                              size="small"
                              color={n.target_group === 'single_user' ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={n.sent_count || 0}
                              size="small"
                              color={n.sent_count > 0 ? 'success' : 'default'}
                              variant={n.sent_count > 0 ? 'filled' : 'outlined'}
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{n.sent_by || 'Admin'}</TableCell>
                          <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                            {n.created_at ? formatDateTime(n.created_at) : '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={histFiltered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }}
              />
            </>
          )}
        </Card>
      )}

      {/* ── Snackbar ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
